/**
 * Investigation Memory — Archive-aware navigation memory (V2.0 permanent).
 *
 * A session-scoped stack of visited Archive pages, each with a full state
 * snapshot (scroll, expanded panels, tabs, filters, timestamp) so readers
 * can always return exactly where they were. Browser history outside the
 * Archive is never touched.
 */
const SS_KEY = "archive_investigation_v1";
const LS_LAST_KEY = "archive_last_investigation";
const LS_DISMISS_KEY = "archive_resume_dismissed";
const MAX_STACK = 40;

const ARCHIVE_PREFIXES = [
  "/topic/", "/people/", "/organizations/", "/operations/",
  "/connections", "/epstein-files", "/spotlight", "/trail/",
];

export const isArchivePath = (pathname) => {
  if (pathname === "/" || pathname === "/archive") return true;
  return ARCHIVE_PREFIXES.some((p) => pathname === p || pathname.startsWith(p));
};

export const titleFor = (url) => {
  const path = url.split("?")[0];
  if (path === "/") return "Home";
  if (path === "/archive") return "The Archive";
  if (path === "/spotlight") return "Spotlight";
  if (path.startsWith("/connections")) return "Connections Explorer";
  if (path.startsWith("/trail/")) return "Shared Trail";
  if (path === "/epstein-files") return "The Epstein Files";
  const seg = path.split("/").filter(Boolean).pop() || path;
  return seg.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const load = () => {
  try {
    const d = JSON.parse(sessionStorage.getItem(SS_KEY));
    return d && Array.isArray(d.stack) ? d : null;
  } catch { return null; }
};

let mem = load() || { stack: [], live: null, startedAt: Date.now() };
if (!mem.journey) {
  mem.journey = { startedAt: mem.startedAt || Date.now(), seen: {}, connections: 0, evidence: 0, sources: 0 };
}
let stateBag = {};
let pageTitle = null;
let pageScroll = 0;
let currentUrl = typeof window !== "undefined"
  ? window.location.pathname + window.location.search : "/";
let pendingRestore = null;  // {url, scroll, state} — survives until next fresh nav
let backNav = false;        // suppress stack-push on our own back/jump navigation
let resumeOffer = null;     // {url, scroll} — offered after an accidental refresh
let version = 0;
const subs = new Set();

// Refresh recovery: same tab reloaded on the same Archive page → hydrate
// component state automatically, offer scroll resume.
if (mem.live && mem.live.url === currentUrl && isArchivePath(currentUrl.split("?")[0])) {
  pendingRestore = mem.live;
  if ((mem.live.scroll || 0) > 300) {
    resumeOffer = { url: mem.live.url, scroll: mem.live.scroll };
  }
}

let persistT = null;
const persist = () => {
  clearTimeout(persistT);
  persistT = setTimeout(() => {
    try {
      sessionStorage.setItem(SS_KEY, JSON.stringify(mem));
      // Cross-session memory for the "Resume your investigation?" card —
      // only content pages count as an investigation worth resuming.
      if (mem.live && /^\/(topic|people|organizations|operations)\//.test(mem.live.url.split("?")[0])) {
        localStorage.setItem(LS_LAST_KEY, JSON.stringify({
          url: mem.live.url, title: mem.live.title,
          scroll: mem.live.scroll, ts: Date.now(),
        }));
      }
    } catch { /* full */ }
  }, 250);
};

const notify = () => { version += 1; subs.forEach((f) => f()); };
export const subscribe = (f) => { subs.add(f); return () => subs.delete(f); };
export const getVersion = () => version;

const snapshot = (url) => ({
  url,
  title: pageTitle || titleFor(url),
  scroll: pageScroll,
  state: { ...stateBag },
  ts: Date.now(),
});

export const beginPage = (url, { samePage = false, wasBack = false, firstMount = false } = {}) => {
  currentUrl = url;
  if (samePage) {
    if (mem.live) { mem.live.url = url; persist(); }
    notify();
    return;
  }
  stateBag = {};
  pageTitle = null;
  pageScroll = 0;
  if (!firstMount) {
    resumeOffer = null;
    if (!wasBack) pendingRestore = null; // never rehydrate stale state forward
  }
  mem.live = isArchivePath(url.split("?")[0]) ? snapshot(url) : null;
  if (mem.live) mem.journey.seen[url.split("?")[0]] = 1;
  persist();
  notify();
};

export const reportScroll = (y) => {
  pageScroll = y;
  if (mem.live && mem.live.url === currentUrl) { mem.live.scroll = y; persist(); }
};

export const setInvestigationTitle = (t) => {
  if (!t || pageTitle === t) return;
  pageTitle = t;
  if (mem.live && mem.live.url === currentUrl) { mem.live.title = t; persist(); }
  notify();
};

export const setStateValue = (key, val) => {
  stateBag[key] = val;
  if (mem.live && mem.live.url === currentUrl) { mem.live.state = { ...stateBag }; persist(); }
};

export const getRestoredValue = (url, key) =>
  pendingRestore && pendingRestore.url === url ? pendingRestore.state?.[key] : undefined;

export const getPendingRestore = () => pendingRestore;

export const pushEntry = (prevUrl) => {
  const entry = snapshot(prevUrl);
  const top = mem.stack[mem.stack.length - 1];
  if (top && top.url === entry.url) mem.stack.pop(); // refreshed revisit replaces
  mem.stack.push(entry);
  if (mem.stack.length > MAX_STACK) mem.stack = mem.stack.slice(-MAX_STACK);
  mem.journey.connections += 1;
  persist();
  notify();
};

export const consumeBackFlag = () => { const b = backNav; backNav = false; return b; };

export const goBackEntry = () => {
  const entry = mem.stack.pop();
  if (!entry) return null;
  pendingRestore = entry;
  backNav = true;
  persist();
  notify();
  return entry;
};

export const jumpToEntry = (index) => {
  if (index < 0 || index >= mem.stack.length) return null;
  const entry = mem.stack[index];
  mem.stack = mem.stack.slice(0, index);
  pendingRestore = entry;
  backNav = true;
  persist();
  notify();
  return entry;
};

export const getStack = () => mem.stack;
export const getResumeOffer = () => resumeOffer;
export const clearResumeOffer = () => { resumeOffer = null; notify(); };
export const getCurrentTitle = () => pageTitle;
export const getStartedAt = () => mem.startedAt;
export const getCurrentUrl = () => currentUrl;

// ---- Investigation Journey (private, non-gamified) ----------------------
export const recordEvidence = () => { mem.journey.evidence += 1; persist(); notify(); };
export const recordSource = () => { mem.journey.sources += 1; persist(); notify(); };
export const getJourney = () => ({
  startedAt: mem.journey.startedAt,
  pages: Object.keys(mem.journey.seen).length,
  connections: mem.journey.connections,
  evidence: mem.journey.evidence,
  sources: mem.journey.sources,
});

// ---- Cross-session resume ("your investigation has been waiting") -------
let resumeNav = false;
export const consumeResumeFlag = () => { const r = resumeNav; resumeNav = false; return r; };
export const resumeExternal = (url, scroll) => {
  pendingRestore = { url, scroll: scroll || 0, state: {} };
  resumeNav = true;
};
export const getLastInvestigation = () => {
  try { return JSON.parse(localStorage.getItem(LS_LAST_KEY)); } catch { return null; }
};
export const dismissLastInvestigation = () => {
  const last = getLastInvestigation();
  if (last) { try { localStorage.setItem(LS_DISMISS_KEY, String(last.ts)); } catch { /* noop */ } }
};
export const isLastInvestigationDismissed = () => {
  const last = getLastInvestigation();
  if (!last) return true;
  try { return localStorage.getItem(LS_DISMISS_KEY) === String(last.ts); } catch { return true; }
};

/**
 * Re-assert a scroll target while async content settles (same technique as
 * ScrollManager). Cancels immediately on any user wheel/touch/key input so
 * we never fight the reader.
 */
export const restoreScroll = (targetY, settleMs = 3000, hardCapMs = 15000) => {
  if (typeof window === "undefined" || !(targetY > 0)) return;
  const start = performance.now();
  let lastGrowth = start;
  let lastHeight = document.documentElement.scrollHeight;
  let cancelled = false;
  const cancel = () => {
    cancelled = true;
    window.removeEventListener("wheel", cancel);
    window.removeEventListener("touchstart", cancel);
    window.removeEventListener("keydown", cancel);
  };
  window.addEventListener("wheel", cancel, { passive: true });
  window.addEventListener("touchstart", cancel, { passive: true });
  window.addEventListener("keydown", cancel);
  const attempt = () => {
    if (cancelled) return;
    const now = performance.now();
    const height = document.documentElement.scrollHeight;
    if (height !== lastHeight) { lastHeight = height; lastGrowth = now; } // layout still moving
    const maxY = Math.max(height - window.innerHeight, 0);
    const y = Math.min(targetY, maxY);
    if (Math.abs(window.scrollY - y) > 2) {
      window.scrollTo(0, y);
      document.documentElement.scrollTop = y;
    }
    const settled = now - lastGrowth > settleMs;
    const reached = Math.abs(window.scrollY - targetY) <= 2;
    if ((settled && reached) || now - start > hardCapMs) cancel();
    else requestAnimationFrame(attempt);
  };
  attempt();
};
